/**
 * Store, types, and validation for RunMetricThreshold configuration.
 * This file is separate from RunMetricThresholdSection.tsx to avoid import cycles.
 * DO NOT import React components or files that import React components here.
 */
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {capitalizeFirstLetter} from '../../../../util/string';
import {reportTriggerConfigParseError} from '../configParseError';
import {EventTriggeringConditionType} from '../TriggerEventTypes';
import {type Trigger} from '../TriggersTable/utilTypes';
import {
  getRunFilterConfigFromTriggerFilter,
  isValidRunFilterConfig,
  type RunFilter,
  type RunFilterConfig,
} from './RunFilterShared';

export type AggOpOption = {
  value: MetricFilterAggOp;
  label: string;
};
// only AVERAGE is supported for now
export type MetricFilterAggOp = 'AVERAGE' | 'MIN' | 'MAX';
const aggOps: MetricFilterAggOp[] = ['AVERAGE', 'MIN', 'MAX']; // fancy type way to do this?
export const aggOpOptions: AggOpOption[] = aggOps.map(aggOp => ({
  value: aggOp,
  label: capitalizeFirstLetter(aggOp.toLowerCase()),
}));

export type CmpOp = '$gt' | '$gte' | '$lt' | '$lte' | '$ne' | '$eq'; // TODO: add more + add UI nice string for these

export type CmpOpOption = {
  value: CmpOp;
  label: string;
};

export const cmpOpOptions: CmpOpOption[] = [
  {value: '$gt', label: 'Above'},
  {value: '$gte', label: 'Above or Equal to'},
  {value: '$lt', label: 'Below'},
  {value: '$lte', label: 'Below or Equal to'},
  {value: '$ne', label: 'Not Equal to'},
  {value: '$eq', label: 'Equal to'},
];

export type MetricFilter = {
  metricName: string;
  threshold: string;
  windowSize: string;
  aggOp: MetricFilterAggOp;
  cmpOp: CmpOpOption;
  panelLink: string | undefined;
};

export type RunMetricThresholdConfig = {
  metricFilter: Partial<MetricFilter> &
    Pick<MetricFilter, 'cmpOp' | 'windowSize' | 'aggOp'>;
  runFilter: RunFilterConfig;
  allowAutoFocus: boolean;
};

const DEFAULT_WINDOW_SIZE = 5;

const initialRunMetricConfig: RunMetricThresholdConfig = {
  metricFilter: {
    metricName: undefined,
    threshold: undefined,
    windowSize: DEFAULT_WINDOW_SIZE.toString(),
    cmpOp: cmpOpOptions[0],
    aggOp: 'AVERAGE',
  },
  runFilter: {
    isUsernameFilterEnabled: false,
    username: undefined,
    isRunNameFilterEnabled: false,
    runName: undefined,
  },
  allowAutoFocus: true,
};

export const useRunMetricThresholdStore = create<RunMetricThresholdConfig>()(
  devtools(
    () => ({
      ...initialRunMetricConfig,
    }),
    {name: 'RunMetricThresholdStore'}
  )
);

export const RunMetricThresholdActions = {
  initAsync: async (suggestedMetricName?: string, panelLink?: string) => {
    useRunMetricThresholdStore.setState({
      ...initialRunMetricConfig,
      metricFilter: {
        ...initialRunMetricConfig.metricFilter,
        metricName: suggestedMetricName,
        panelLink: panelLink,
      },
      allowAutoFocus: suggestedMetricName == null,
    });
  },
  initFromTrigger: (trigger: Trigger) => {
    const config = getRunMetricThresholdConfig(trigger);
    if (config) {
      useRunMetricThresholdStore.setState(config);
    }
  },

  setMetricName: (metricName: string) => {
    setMetricFilter({metricName});
  },
  setThreshold: (threshold: string) => {
    setMetricFilter({threshold});
  },
  setCmpOp: (cmpOpOption: CmpOpOption) => {
    setMetricFilter({cmpOp: cmpOpOption});
  },
  setRunName: (runName: string) => {
    setRunFilter({runName});
  },
  setWindowSize: (windowSize: string) => {
    setMetricFilter({windowSize});
  },
  setIsRunNameFilterEnabled: (isRunNameFilterEnabled: boolean) => {
    setRunFilter({isRunNameFilterEnabled});
  },
  setUsername: (username: string | undefined) => {
    setRunFilter({username});
  },
  setIsUsernameFilterEnabled: (isUsernameFilterEnabled: boolean) => {
    setRunFilter({isUsernameFilterEnabled});
  },
  setAggOp: (aggOp: MetricFilterAggOp) => {
    setMetricFilter({aggOp});
  },
};

const setMetricFilter = (metricFilter: Partial<MetricFilter>) => {
  useRunMetricThresholdStore.setState(state => ({
    metricFilter: {...state.metricFilter, ...metricFilter},
  }));
};

const setRunFilter = (runFilter: Partial<RunFilter>) => {
  useRunMetricThresholdStore.setState(state => ({
    runFilter: {...state.runFilter, ...runFilter},
  }));
};

export const isValidMetricName = (metricName: string | undefined) => {
  return metricName !== undefined && metricName.trim() !== '';
};

export const getNumberValueForField = (field: string | undefined) => {
  const fieldNumber = Number(field || '');
  return fieldNumber;
};

export const isValidThreshold = (threshold: string | undefined) => {
  if (threshold === undefined || threshold === '') {
    return false;
  }
  const thresholdNumber = getNumberValueForField(threshold);
  return !isNaN(thresholdNumber) && isFinite(thresholdNumber);
};

export const isValidWindowSize = (windowSize: string | undefined) => {
  if (windowSize === undefined || windowSize === '') {
    return false;
  }
  const windowSizeNumber = getNumberValueForField(windowSize);
  return (
    Number.isInteger(windowSizeNumber) &&
    windowSizeNumber >= 1 &&
    windowSizeNumber <= 100
  );
};

export const isValidRunMetricThresholdConfig = (
  config: RunMetricThresholdConfig
) => {
  return (
    isValidMetricName(config.metricFilter.metricName) &&
    isValidThreshold(config.metricFilter.threshold) &&
    isValidWindowSize(config.metricFilter.windowSize) &&
    // cmpOp is asserted to always be valid by types
    // aggOp is currently hardcoded. For the futurenote that undefined is a valid
    //   value for aggOp - it means "take the last value and ignore windowSize"
    //   which is not something the UI currently supports, but it's not an error
    isValidRunFilterConfig(config.runFilter)
  );
};

export const getEventConfigForRunMetric = (
  config: RunMetricThresholdConfig
) => {
  return {
    eventTriggeringConditionType: EventTriggeringConditionType.RunMetric,
    filters: {
      filters: [
        {
          metricFilter: {...config.metricFilter, aggOp: 'AVERAGE'},
          runFilter: config.runFilter,
        },
      ],
    },
  };
};

// this is used when we're looking at a already created trigger in the UI
export const getRunMetricThresholdConfig = (
  trigger: Trigger
): RunMetricThresholdConfig | undefined => {
  if (
    !trigger.triggeringCondition.filter ||
    trigger.triggeringCondition.eventType !==
      EventTriggeringConditionType.RunMetric
  ) {
    return undefined;
  }

  try {
    const parsedFilter = JSON.parse(trigger.triggeringCondition.filter);

    const runFilter = getRunFilterConfigFromTriggerFilter(parsedFilter);
    if (!runFilter) {
      reportTriggerConfigParseError('run_metric_threshold', trigger);
      return undefined;
    }
    if (
      !parsedFilter.metric_filter &&
      !parsedFilter.run_metric_filter?.threshold_filter
    ) {
      reportTriggerConfigParseError('run_metric_threshold', trigger);
      return undefined;
    }
    const rawMetricFilter = parsedFilter.metric_filter
      ? JSON.parse(parsedFilter.metric_filter)
      : parsedFilter.run_metric_filter.threshold_filter;

    return {
      metricFilter: {
        metricName: rawMetricFilter.name,
        threshold: rawMetricFilter.threshold.toString(),
        windowSize: rawMetricFilter.window_size,
        cmpOp:
          cmpOpOptions.find(op => op.value === rawMetricFilter.cmp_op) ??
          cmpOpOptions[0],
        aggOp: rawMetricFilter.agg_op,
        panelLink: rawMetricFilter.panel_link,
      },
      runFilter,
      // Autofocus doesn't make sense when viewing UI
      // It's a bit odd to set this when reading from data received from the server,
      // So potentially in the future we should split up the config and only return
      // metric/runFilter and then have a separate config for UI state.
      allowAutoFocus: false,
    };
  } catch (e) {
    reportTriggerConfigParseError('run_metric_threshold', trigger, e);
    return undefined;
  }
};

export const getAggOpStr = (aggOp: string | undefined) => aggOp?.toLowerCase();
